/**
 * Bandwidth estimation via RTCPeerConnection.getStats() polling.
 *
 * Monitors connection quality metrics: available outgoing bitrate,
 * packet loss rate, jitter, and round-trip time. Reports every
 * 10 seconds (5 polls at 2s interval).
 */

/** Bandwidth and quality metrics for a single peer connection */
export interface BandwidthEstimate {
  /** Available outgoing bitrate in bits/sec from candidate-pair stats */
  availableOutgoingBitrate: number | null
  /** Fraction of packets lost (0.0 to 1.0) over the last interval */
  packetsLost: number
  /** Maximum jitter across inbound RTP streams in seconds */
  jitter: number
  /** Current round-trip time in seconds from the active candidate pair */
  roundTripTime: number | null
  /** Quality limitation reason for outbound video (none/cpu/bandwidth/other) */
  qualityLimitationReason: string | null
  /** Timestamp of this estimate */
  timestamp: number
}

/** Internal snapshot of raw stats extracted from getStats() */
interface StatsSnapshot {
  timestamp: number
  inbound: Array<{
    kind: string
    packetsReceived: number
    packetsLost: number
    bytesReceived: number
    jitter: number
  }>
  outbound: Array<{
    kind: string
    packetsSent: number
    bytesSent: number
    qualityLimitationReason: string | null
  }>
  candidatePair: {
    currentRoundTripTime: number | null
    availableOutgoingBitrate: number | null
  } | null
}

/** Per-peer monitoring state */
interface MonitorState {
  intervalId: ReturnType<typeof setInterval>
  prevSnapshot: StatsSnapshot | null
  history: BandwidthEstimate[]
  reportIntervalId: ReturnType<typeof setInterval> | null
}

/** Active monitors keyed by a caller-supplied identifier */
const monitors = new Map<string, MonitorState>()

/**
 * Extract relevant metrics from an RTCStatsReport.
 */
function extractSnapshot(stats: RTCStatsReport): StatsSnapshot {
  const snapshot: StatsSnapshot = {
    timestamp: Date.now(),
    inbound: [],
    outbound: [],
    candidatePair: null,
  }

  stats.forEach((report) => {
    switch (report.type) {
      case 'inbound-rtp':
        snapshot.inbound.push({
          kind: report.kind,
          packetsReceived: report.packetsReceived ?? 0,
          packetsLost: report.packetsLost ?? 0,
          bytesReceived: report.bytesReceived ?? 0,
          jitter: report.jitter ?? 0,
        })
        break

      case 'outbound-rtp':
        snapshot.outbound.push({
          kind: report.kind,
          packetsSent: report.packetsSent ?? 0,
          bytesSent: report.bytesSent ?? 0,
          qualityLimitationReason: report.qualityLimitationReason ?? null,
        })
        break

      case 'candidate-pair':
        if (report.state === 'succeeded') {
          snapshot.candidatePair = {
            currentRoundTripTime: report.currentRoundTripTime ?? null,
            availableOutgoingBitrate: report.availableOutgoingBitrate ?? null,
          }
        }
        break
    }
  })

  return snapshot
}

/**
 * Compute a BandwidthEstimate delta from two consecutive snapshots.
 */
function computeEstimate(
  prev: StatsSnapshot,
  curr: StatsSnapshot,
): BandwidthEstimate {
  let totalPacketsLost = 0
  let totalPacketsReceived = 0
  let jitterMax = 0

  for (let i = 0; i < curr.inbound.length; i++) {
    const p = prev.inbound[i]
    const c = curr.inbound[i]
    if (p && c) {
      totalPacketsLost += c.packetsLost - p.packetsLost
      totalPacketsReceived += c.packetsReceived - p.packetsReceived
      jitterMax = Math.max(jitterMax, c.jitter)
    }
  }

  const packetLossRate =
    totalPacketsReceived > 0
      ? totalPacketsLost / (totalPacketsReceived + totalPacketsLost)
      : 0

  const videoOutbound = curr.outbound.find((o) => o.kind === 'video')

  return {
    availableOutgoingBitrate:
      curr.candidatePair?.availableOutgoingBitrate ?? null,
    packetsLost: Math.max(0, packetLossRate),
    jitter: jitterMax,
    roundTripTime: curr.candidatePair?.currentRoundTripTime ?? null,
    qualityLimitationReason: videoOutbound?.qualityLimitationReason ?? null,
    timestamp: curr.timestamp,
  }
}

/**
 * Start monitoring a peer connection. Polls getStats() every 2 seconds.
 * Reports are accumulated and available via getEstimatedBandwidth().
 *
 * @param id - A unique identifier for this monitor (typically the peerId)
 * @param pc - The RTCPeerConnection to monitor
 * @param onReport - Optional callback invoked every 10 seconds with the latest estimate
 */
export function startMonitoring(
  id: string,
  pc: RTCPeerConnection,
  onReport?: (estimate: BandwidthEstimate) => void,
): void {
  // Clean up any existing monitor for this id
  stopMonitoring(id)

  const state: MonitorState = {
    intervalId: setInterval(async () => {
      try {
        const stats = await pc.getStats()
        const snapshot = extractSnapshot(stats)

        if (state.prevSnapshot) {
          const estimate = computeEstimate(state.prevSnapshot, snapshot)
          state.history.push(estimate)
          // Keep rolling window of 30 samples (60 seconds at 2s interval)
          if (state.history.length > 30) {
            state.history.shift()
          }
        }

        state.prevSnapshot = snapshot
      } catch {
        // Connection may have closed -- silently ignore
      }
    }, 2000),
    prevSnapshot: null,
    history: [],
    reportIntervalId: null,
  }

  // Set up 10-second reporting interval if callback provided
  if (onReport) {
    state.reportIntervalId = setInterval(() => {
      const latest = state.history[state.history.length - 1]
      if (latest) {
        onReport(latest)
      }
    }, 10000)
  }

  monitors.set(id, state)
}

/**
 * Stop monitoring a peer connection and clean up intervals.
 */
export function stopMonitoring(id: string): void {
  const state = monitors.get(id)
  if (!state) return

  clearInterval(state.intervalId)
  if (state.reportIntervalId) {
    clearInterval(state.reportIntervalId)
  }
  monitors.delete(id)
}

/**
 * Get the most recent bandwidth estimate for a monitored connection.
 * Returns null if no estimate is available yet (requires at least 2 polls).
 */
export function getEstimatedBandwidth(id: string): BandwidthEstimate | null {
  const state = monitors.get(id)
  if (!state || state.history.length === 0) return null
  return state.history[state.history.length - 1]
}

/**
 * Get the full history of bandwidth estimates for a monitored connection.
 * Up to 30 samples (60 seconds of data at 2s polling).
 */
export function getBandwidthHistory(id: string): readonly BandwidthEstimate[] {
  const state = monitors.get(id)
  if (!state) return []
  return state.history
}

/**
 * Stop all active monitors. Call on meeting end.
 */
export function stopAllMonitoring(): void {
  for (const id of monitors.keys()) {
    stopMonitoring(id)
  }
}
